import React, { useCallback, useMemo } from "react";

import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import useAnalytics from "@app/util/contexts/useAnalytics";

import CreateEmailContext from "../CreateEmailContext";
import SendAndCreateEmailButton from "../SendAndCreateEmailButton";

import styles from "./CreateEmailModal.module.scss";
import messages from "./messages";

interface EmailActionButtonProps {
  emailSending: boolean;
  onClick: () => void;
  openActivityModal: boolean;
}

const EmailActionButton: React.FC<EmailActionButtonProps> = ({
  emailSending,
  onClick,
  openActivityModal,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const { emailFilled, recipients, templateChanged, validationError } =
    useNonNullableContext(CreateEmailContext);

  const disabledTooltip = useMemo(() => {
    if (validationError) {
      return validationError;
    }

    if (!emailFilled || !recipients.length) {
      return intl.formatMessage({
        id: "email.creation.nextDisabledTooltip",
        defaultMessage: "Fill out subject, body and recipient to send email.",
        description: "Next disabled tooltip",
      });
    }
  }, [intl, emailFilled, recipients, validationError]);

  const disabled = emailSending || !!disabledTooltip;

  const handleClick = useCallback(() => {
    const analyticPart = openActivityModal ? "Next" : "Send and Create";
    analytics.clicked([analyticPart]);
    onClick();
  }, [analytics, onClick, openActivityModal]);

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "email.creation.nextConfirmationNo",
        defaultMessage: "Cancel",
        description: "Next confirmation no",
      })}
      description={intl.formatMessage(messages.nextConfirmationTitle)}
      disabled={!templateChanged}
      okText={intl.formatMessage(messages.nextConfirmationYes)}
      onConfirm={handleClick}
      overlayClassName={styles.confirmation}
    >
      <Tooltip title={disabledTooltip}>
        <span>
          {openActivityModal ? (
            <Button
              disabled={disabled}
              leftIcon={<FontAwesomeIcon icon={faArrowRight} />}
              loading={emailSending}
              onClick={templateChanged ? undefined : handleClick}
              type="primary"
            >
              {intl.formatMessage(messages.next)}
            </Button>
          ) : (
            <SendAndCreateEmailButton
              disabled={disabled}
              loading={emailSending}
              onClick={templateChanged ? undefined : handleClick}
              recipientCount={recipients.length}
            />
          )}
        </span>
      </Tooltip>
    </Popconfirm>
  );
};

export default EmailActionButton;
